import { Routes, Route } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ThemeProvider } from '../components/theme-provider';
import { TooltipProvider } from '../components/ui/tooltip';
import { Toaster } from '../components/ui/toaster';
import { AuthProvider } from '../contexts/auth-context';
import { useAuth } from '../hooks/useAuth';
import { GymAdminProvider, useGymAdmin } from './context/gym-admin-context';
import { GymAdminLayout } from './layout/GymAdminLayout';
import { RequireRoles, DefaultRedirect } from './guards/RequireRoles';
import { PlaceGate, EnterPanel } from './guards/PlaceGate';
import GymSignIn from './pages/sign-in';
import NoGyms from './pages/no-gyms';
import NoAccess from './pages/no-access';
import InfoPage from './pages/info';
import SponsorsPage from './pages/sponsors';
import HoursPage from './pages/hours';
import StaffPage from './pages/staff';
import MembersPage from './pages/members';
import StagesPage from './pages/stages';
import CategoriesPage from './pages/categories';
import TeamsPage from './pages/teams';
import TeamManagersPage from './pages/team-managers';
import TrophyParticipantsPage from './pages/trophy-participants';
import RoundsPage from './pages/rounds';
import RoundDetailPage from './pages/round-detail';
import RoundScoresPage from './pages/round-scores';
import RoundScoresRedirect from './pages/round-scores-redirect';
import RoundBlocksRedirect from './pages/round-blocks-redirect';
import StandingsPage from './pages/standings';
import RegistrationsPage from './pages/registrations';
import MemberEditPage from './pages/member-edit';
import MembershipOrganizationsPage from './pages/membership-organizations';
import DpaPage from './pages/dpa';
import RouteSettingPage from './pages/route-setting';
import CragRoutesPage from './pages/crag-routes';
import ReportsPage from './pages/reports';
import CragModelPage from './pages/crag-model';
import RouteSettersPage from './pages/route-setters';
import BouldersBoardPage from './pages/boulders-board';
import RoutesBoardPage from './pages/routes-board';
import ScansPage from './pages/scans';
import AnnouncementsPage from './pages/announcements';
import DevicesPage from './pages/devices';
import DeviceEditPage from './pages/device-edit';
import BoardsPage from './pages/boards';
import BoardDashboardPage from './pages/board-dashboard';

function GymAdminRoutes() {
  const { isAuthenticated, loading } = useAuth();
  const { staffGyms } = useGymAdmin();

  if (loading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <GymSignIn />;
  }

  if (staffGyms.length === 0) {
    return <NoGyms />;
  }

  // The place is the first segment of the URL: `/junglerock/info`,
  // `/tlom/stages`. `PlaceGate` checks it names one of the user's places and
  // rescues links that predate the segment (`/info`); the bare root goes to
  // the place the reader managed last.
  return (
    <Routes>
      <Route index element={<EnterPanel />} />
      <Route path=":placeSlug" element={<PlaceGate />}>
        <Route element={<GymAdminLayout />}>
          <Route index element={<DefaultRedirect />} />
          <Route
            path="info"
            element={
              <RequireRoles path="/info" roles={['admin']}>
                <InfoPage />
              </RequireRoles>
            }
          />
          <Route
            path="hours"
            element={
              <RequireRoles path="/hours" roles={['admin', 'editor']}>
                <HoursPage />
              </RequireRoles>
            }
          />
          {/* A circuit's or a competition's sponsors (issue #374). */}
          <Route
            path="sponsors"
            element={
              <RequireRoles path="/sponsors" roles={['admin']}>
                <SponsorsPage />
              </RequireRoles>
            }
          />
          <Route
            path="staff"
            element={
              <RequireRoles path="/staff" roles={['admin']}>
                <StaffPage />
              </RequireRoles>
            }
          />
          <Route
            path="members"
            element={
              <RequireRoles path="/members" roles={['admin', 'member_manager']}>
                <MembersPage />
              </RequireRoles>
            }
          />
          <Route
            path="members/organizations"
            element={
              <RequireRoles path="/members/organizations" roles={['admin', 'member_manager']}>
                <MembershipOrganizationsPage />
              </RequireRoles>
            }
          />
          <Route
            path="members/:id/edit"
            element={
              <RequireRoles path="/members" roles={['admin', 'member_manager']}>
                <MemberEditPage />
              </RequireRoles>
            }
          />
          {/* The classes the circuit runs. An athlete carries one for the
              season, so a roster row and every registration made from it start
              in the right place. */}
          <Route
            path="categories"
            element={
              <RequireRoles path="/categories" roles={['admin']}>
                <CategoriesPage />
              </RequireRoles>
            }
          />
          {/* The squads entrants turn up with, on the same reasoning: a team
              enters the season, not one weekend. Open to the desk as well as to
              the admins — the person entering people is who meets a squad
              nobody has written down yet. */}
          <Route
            path="teams"
            element={
              <RequireRoles path="/teams" roles={['admin', 'member_manager']}>
                <TeamsPage />
              </RequireRoles>
            }
          />
          {/* Who speaks for each squad at this competition. On the stage, because
              an appointment is for one weekend. */}
          <Route
            path="team-managers"
            element={
              <RequireRoles path="/team-managers" roles={['admin']}>
                <TeamManagersPage />
              </RequireRoles>
            }
          />
          {/* Everybody taking part in the circuit, gathered across its stages.
              Not a roster — a trophy keeps none. */}
          <Route
            path="participants"
            element={
              <RequireRoles path="/participants" roles={['admin', 'member_manager']}>
                <TrophyParticipantsPage />
              </RequireRoles>
            }
          />
          {/* The circuit's stages. Creating one is what a trophy admin is here
              for; "manage" then switches the sidebar to the stage itself. */}
          <Route
            path="stages"
            element={
              <RequireRoles path="/stages" roles={['admin']}>
                <StagesPage />
              </RequireRoles>
            }
          />
          {/* The gym's side of the same list: the events it holds and the stages
              it hosts. Same page — a competition is the same record from either
              side — under the entry the gym's menu has for it. */}
          <Route
            path="events"
            element={
              <RequireRoles path="/events" roles={['admin']}>
                <StagesPage />
              </RequireRoles>
            }
          />
          {/* The rounds entry, which has no list of its own: it opens the first
              round, or says there are none. */}
          <Route
            path="rounds"
            element={
              <RequireRoles path="/rounds" roles={['admin', 'editor']}>
                <RoundsPage />
              </RequireRoles>
            }
          />
          {/* A round *is* its scoreboard: one tab per class, one row per
              entrant, cards corrected in place, the clock run from the top of
              it. The screen the competition is actually run from, so it is the
              round's own URL and what the menu's row opens. */}
          <Route
            path="rounds/:roundId"
            element={
              <RequireRoles path="/rounds" roles={['admin', 'editor']}>
                <RoundScoresPage />
              </RequireRoles>
            }
          />
          {/* Setting that round up: its name, its clock's length, and the
              classes and blocks it runs. Guarded on the entry's own nav row —
              it is the same section. */}
          <Route
            path="rounds/:roundId/edit"
            element={
              <RequireRoles path="/rounds" roles={['admin', 'editor']}>
                <RoundDetailPage />
              </RequireRoles>
            }
          />
          {/* Where the scoreboard used to live. Links to it are out in the world
              — in a browser's history, in a message to the desk — so they are
              moved rather than broken, and the open class and filter travel
              with them. */}
          <Route path="rounds/:roundId/scores" element={<RoundScoresRedirect />} />
          {/* Each class's standings across its rounds: its turns as one table.
              Read off the rounds' cards by the people who run them. */}
          <Route
            path="standings"
            element={
              <RequireRoles path="/standings" roles={['admin', 'editor']}>
                <StandingsPage />
              </RequireRoles>
            }
          />
          {/* Likewise the by-block table, which is a way of reading a round
              rather than a page: it lives on the round's board now, behind the
              Athlete/Block switch. No roles on it — it only forwards, and where
              it forwards to is guarded. */}
          <Route path="blocks" element={<RoundBlocksRedirect />} />
          {/* One stage's entry list, drawn from its circuit's roster. */}
          <Route
            path="registrations"
            element={
              <RequireRoles path="/registrations" roles={['admin', 'member_manager']}>
                <RegistrationsPage />
              </RequireRoles>
            }
          />
          <Route
            path="dpa"
            element={
              <RequireRoles path="/dpa" roles={['admin']}>
                <DpaPage />
              </RequireRoles>
            }
          />
          <Route
            path="route-setting"
            element={
              <RequireRoles path="/route-setting" roles={['admin', 'editor', 'routesetter']}>
                <RouteSettingPage />
              </RequireRoles>
            }
          />
          <Route
            path="route-setters"
            element={
              <RequireRoles path="/route-setters" roles={['admin', 'routesetter']}>
                <RouteSettersPage />
              </RequireRoles>
            }
          />
          <Route
            path="crag-routes"
            element={
              <RequireRoles path="/crag-routes" roles={['admin', 'editor', 'routesetter']}>
                <CragRoutesPage />
              </RequireRoles>
            }
          />
          <Route
            path="model-3d"
            element={
              <RequireRoles path="/model-3d" roles={['admin']}>
                <CragModelPage />
              </RequireRoles>
            }
          />
          <Route
            path="reports"
            element={
              <RequireRoles path="/reports" roles={['admin', 'editor']}>
                <ReportsPage />
              </RequireRoles>
            }
          />
          <Route
            path="boulders-board"
            element={
              <RequireRoles path="/boulders-board" roles={['admin', 'routesetter']}>
                <BouldersBoardPage />
              </RequireRoles>
            }
          />
          <Route
            path="routes-board"
            element={
              <RequireRoles path="/routes-board" roles={['admin', 'routesetter']}>
                <RoutesBoardPage />
              </RequireRoles>
            }
          />
          <Route
            path="scans"
            element={
              <RequireRoles path="/scans" roles={['admin', 'editor', 'routesetter']}>
                <ScansPage />
              </RequireRoles>
            }
          />
          <Route
            path="announcements"
            element={
              <RequireRoles path="/announcements" roles={['admin', 'editor']}>
                <AnnouncementsPage />
              </RequireRoles>
            }
          />
          <Route
            path="devices"
            element={
              <RequireRoles path="/devices" roles={['admin']}>
                <DevicesPage />
              </RequireRoles>
            }
          />
          <Route
            path="devices/:id/edit"
            element={
              <RequireRoles path="/devices" roles={['admin']}>
                <DeviceEditPage />
              </RequireRoles>
            }
          />
          <Route
            path="boards"
            element={
              <RequireRoles path="/boards" roles={['admin', 'editor']}>
                <BoardsPage />
              </RequireRoles>
            }
          />
          {/* One board, managed: the photo and the problems on it with their
              usage. Drilled into from the list above, so no menu entry of its own. */}
          <Route
            path="boards/:boardId"
            element={
              <RequireRoles path="/boards" roles={['admin', 'editor']}>
                <BoardDashboardPage />
              </RequireRoles>
            }
          />
          <Route path="no-access" element={<NoAccess />} />
          <Route path="*" element={<DefaultRedirect />} />
        </Route>
      </Route>
    </Routes>
  );
}

/**
 * Root of the admin app served on gym.divepeaks.* and admin.divepeaks.*. Reuses
 * the main app's
 * auth (Devise token) and API client, but with its own desktop-first sidebar
 * shell and permission-gated navigation.
 */
export default function GymAdminApp() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="divepeaks-gym-theme">
      <TooltipProvider>
        <AuthProvider>
          <GymAdminProvider>
            <GymAdminRoutes />
            <Toaster />
          </GymAdminProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}
